import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

// Decode the small non-sensitive 'last signed-in user' hint cookie the backend
// sets on every successful login/register. Returns null when absent or invalid.
function readLastUserHint() {
  if (typeof document === "undefined") return null;
  const match = document.cookie.split("; ").find((c) => c.startsWith("bf_last_user="));
  if (!match) return null;
  try {
    let raw = match.slice("bf_last_user=".length);
    // Strip RFC-6265 quotes if any (defensive — current backend uses unpadded url-safe b64).
    raw = decodeURIComponent(raw).replace(/^"(.*)"$/, "$1");
    // Re-add padding + convert url-safe alphabet back to standard for atob.
    const pad = "=".repeat((4 - (raw.length % 4)) % 4);
    const standardB64 = (raw + pad).replace(/-/g, "+").replace(/_/g, "/");
    const hint = JSON.parse(atob(standardB64));
    if (!hint?.email || !hint?.name) return null;
    return hint;
  } catch {
    return null;
  }
}

function clearLastUserHint() {
  document.cookie = "bf_last_user=; path=/; max-age=0; samesite=none; secure";
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [lastUser, setLastUser] = useState(null);
  const passwordRef = useRef(null);

  // Read the "last user" hint once on mount.
  useEffect(() => {
    setLastUser(readLastUserHint());
  }, []);

  // If we already have a live session, skip the login form entirely.
  useEffect(() => {
    if (!user) return;
    if (user.role === "admin") navigate("/admin", { replace: true });
    else if (user.role === "publisher") navigate("/publisher", { replace: true });
    else navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}`);
      if (u.role === "admin") navigate("/admin");
      else if (u.role === "publisher") navigate("/publisher");
      else navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // "Continue as <name>" — picks the right flow based on how they last signed in.
  const continueAs = () => {
    if (!lastUser) return;
    if (lastUser.provider === "google") {
      googleLogin();
    } else {
      // Password user: pre-fill email and focus password
      setEmail(lastUser.email);
      setLastUser(null);
      setTimeout(() => passwordRef.current?.focus(), 50);
    }
  };

  const useDifferentAccount = () => {
    clearLastUserHint();
    setLastUser(null);
  };

  const initials = (lastUser?.name || "?")
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 py-12"
      style={{ background: "var(--bf-bg)" }}
      data-testid="login-page"
    >
      <div
        className="w-full max-w-md p-10 rounded-lg"
        style={{ background: "white", border: "1px solid var(--bf-border)" }}
      >
        <Link to="/" className="font-serif text-2xl mb-2 block" data-testid="login-brand">Bookforge</Link>
        <h1 className="font-serif text-3xl mb-1">Welcome back</h1>
        <p className="text-sm mb-8" style={{ color: "var(--bf-text-muted)" }}>
          Sign in to keep writing.
        </p>

        {/* "Continue as <name>" banner — shows only when a previous login hint exists */}
        {lastUser && (
          <div
            className="mb-6 p-4 rounded-lg flex items-center gap-3"
            style={{ background: "var(--bf-surface)", border: "1px solid var(--bf-border)" }}
            data-testid="login-continue-as"
          >
            {lastUser.picture ? (
              <img src={lastUser.picture} alt={lastUser.name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0"
                style={{ background: "var(--bf-primary)", color: "white" }}
                data-testid="login-continue-as-initials"
              >
                {initials || "?"}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate" data-testid="login-continue-as-name">{lastUser.name}</p>
              <p className="text-xs truncate" style={{ color: "var(--bf-text-muted)" }} data-testid="login-continue-as-email">{lastUser.email}</p>
            </div>
            <Button
              size="sm"
              className="text-white flex-shrink-0"
              style={{ background: "var(--bf-primary)" }}
              onClick={continueAs}
              data-testid="login-continue-as-btn"
            >
              Continue
            </Button>
          </div>
        )}

        {lastUser && (
          <p className="text-xs text-center mb-5">
            <button
              type="button"
              onClick={useDifferentAccount}
              className="underline"
              style={{ color: "var(--bf-text-muted)" }}
              data-testid="login-use-different"
            >
              Use a different account
            </button>
          </p>
        )}

        <Button
          type="button"
          variant="outline"
          className="w-full h-11 mb-5"
          onClick={googleLogin}
          data-testid="login-google-btn"
        >
          Continue with Google
        </Button>

        <div className="flex items-center gap-3 my-5 text-xs" style={{ color: "var(--bf-text-muted)" }}>
          <div className="flex-1 h-px" style={{ background: "var(--bf-border)" }} />
          OR EMAIL
          <div className="flex-1 h-px" style={{ background: "var(--bf-border)" }} />
        </div>

        <form onSubmit={submit} className="space-y-5">
          <div>
            <Label htmlFor="email" className="mb-2 block">Email</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
          </div>
          <div>
            <Label htmlFor="password" className="mb-2 block">Password</Label>
            <Input ref={passwordRef} id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-11 text-white"
            style={{ background: "var(--bf-primary)" }}
            data-testid="login-submit-btn"
          >
            {loading ? "Signing in..." : "Sign in"}
          </Button>
        </form>

        <p className="text-sm mt-6 text-center" style={{ color: "var(--bf-text-muted)" }}>
          New here?{" "}
          <Link to="/register" className="underline" data-testid="login-to-register">Create an account</Link>
        </p>
      </div>
    </div>
  );
}
